import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.142 1.594 5.945L0 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.581 0 11.94-5.335 11.943-11.893a11.821 11.821 0 0 0-3.476-8.443z"/>
  </svg>
);

/**
 * WhatsApp-first CTA — single source of truth for the trader signup path.
 *
 * Reads REACT_APP_WHATSAPP_NUMBER (digits only, e.g. "2348012345678"):
 *   - If set: renders an external link to wa.me with a pre-filled greeting.
 *   - If empty: gracefully degrades to /signin so the site works pre-launch.
 *
 * This is how we keep the trader experience "WhatsApp-first" by positioning,
 * not by deleting the web surface — the moment Meta approves and you drop
 * the WABA number into env, every Start CTA across the app flips automatically.
 */
export function useWhatsAppCta() {
  const raw = process.env.REACT_APP_WHATSAPP_NUMBER || "";
  const num = raw.replace(/\D/g, "");
  if (num.length >= 10) {
    const text = encodeURIComponent("Hi Kobolog AI, I want to set up my shop 🛍️");
    return { href: `https://wa.me/${num}?text=${text}`, external: true, channel: "whatsapp" };
  }
  return { href: "/signin", external: false, channel: "web" };
}

export default function WhatsAppCta({
  label = "Chat on WhatsApp",
  fallbackLabel = "Start free",
  className = "",
  size = "lg",
  variant = "primary",
  testid = "wa-cta",
  showArrow = true,
}) {
  const cta = useWhatsAppCta();
  const isWA = cta.channel === "whatsapp";
  const displayLabel = isWA ? label : fallbackLabel;

  const base = "inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-all active:scale-[0.98]";
  const sizeCls = size === "sm" ? "px-4 py-2 text-sm" : "px-6 py-3.5";

  const variants = {
    primary: isWA
      ? "bg-[#25D366] text-white hover:bg-[#1eb955] shadow-md hover:shadow-lg"
      : "bg-kudi-green text-white hover:bg-kudi-green-dark shadow-md",
    outline: isWA
      ? "bg-white border-2 border-[#25D366] text-[#1B7A3A] hover:bg-[#25D366]/5"
      : "bg-white border-2 border-kudi-ink/15 text-kudi-ink/85 hover:border-kudi-green hover:text-kudi-green",
    ghost:
      "bg-white text-kudi-ink border border-border hover:bg-accent",
    inverse: isWA
      ? "bg-[#25D366] text-white hover:bg-[#1eb955]"
      : "bg-white text-kudi-green hover:bg-kudi-cream",
  };
  const cls = `${base} ${sizeCls} ${variants[variant] || variants.primary} ${className}`;

  const content = (
    <>
      {isWA && <WhatsAppIcon className={size === "sm" ? "w-4 h-4" : "w-5 h-5"} />}
      {displayLabel}
      {showArrow && !isWA && <ArrowRight className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />}
    </>
  );

  if (cta.external) {
    return (
      <a href={cta.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid} data-cta-channel="whatsapp">
        {content}
      </a>
    );
  }
  return (
    <Link to={cta.href} className={cls} data-testid={testid} data-cta-channel="web">
      {content}
    </Link>
  );
}

/** Compact secondary link: "Or sign up on web →" — only shown when primary is WhatsApp. */
export function WebFallbackLink({ className = "", testid = "web-fallback-link" }) {
  const cta = useWhatsAppCta();
  if (cta.channel !== "whatsapp") return null; // already on web path
  return (
    <Link to="/signin" className={`text-xs text-kudi-ink/55 hover:text-kudi-green underline underline-offset-4 ${className}`} data-testid={testid}>
      Or sign up on web →
    </Link>
  );
}
